const UPLOAD_FORMAT_LABELS = { '': 'pdf', docx: 'docx', pptx: 'pptx', jpg: 'jpg' };

function uploadFormatLabel(uploadType) {
  const key = uploadType == null ? '' : String(uploadType);
  return (UPLOAD_FORMAT_LABELS[key] ?? key) || 'pdf';
}

function recordNotFoundActionLabel(value) {
  if (value === 'ignore') return 'Ignore';
  if (value === 'warn') return 'Warn';
  if (value === 'create') return 'Create';
  return 'Error';
}

function onEmptyRecordActionLabel(value) {
  return value === 'ignore' ? 'Ignore' : 'Create';
}

function capitalizeAction(action) {
  if (!action) return '';
  const s = String(action);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function refNameById(references) {
  const map = {};
  (references || []).forEach((r) => {
    map[r.id] = r.name || r.tableSchema?.tableName || r.id;
  });
  return map;
}

function fieldLabelFromRef(references, referenceId, fieldId) {
  if (fieldId == null || fieldId === '') return '';
  const ref = (references || []).find((r) => r.id === referenceId);
  const field = ref?.tableSchema?.fields?.find((f) => String(f.id) === String(fieldId));
  return field?.label || String(fieldId);
}

function hostFieldLabel(references, link, fieldId) {
  if (fieldId == null || fieldId === '') return '';
  const hostTableId = link?.table?.id;
  if (!hostTableId) return String(fieldId);
  const hostRef = (references || []).find((r) => String(r.tableId) === String(hostTableId));
  const field = hostRef?.tableSchema?.fields?.find((f) => String(f.id) === String(fieldId));
  return field?.label || String(fieldId);
}

function targetNameForRef(targets, refNameByIdMap, refId) {
  const fromTarget = (targets || []).find((x) => x.referenceId === refId);
  if (fromTarget?.referenceName) return fromTarget.referenceName;
  return refNameByIdMap[refId] || refId;
}

function formatPasskeyEntryLine(targetName, fieldLabel, placeholder) {
  let line = `${targetName} → ${fieldLabel}`;
  const ph = String(placeholder || '').trim();
  if (ph) line += ` (placeholder: "${ph}")`;
  return line;
}

function formatUploadSummaryFromWizard(state) {
  const uploadLabel = state.linkUpload?.uploadField?.label;
  const filenameLabel = state.linkUpload?.filenameField?.label;
  if (!uploadLabel && !filenameLabel) return 'None';
  const refId = state.linkUpload?.referenceId;
  if (!refId) return 'None';
  const targetName = targetNameForRef(state.targets, {}, refId);
  const type = uploadFormatLabel(state.linkUpload.uploadType);
  const parts = [];
  if (uploadLabel) parts.push(`${targetName} → ${uploadLabel} (.${type})`);
  else if (filenameLabel) parts.push(`${targetName} → download name: ${filenameLabel}`);
  if (uploadLabel && filenameLabel) {
    parts.push(`download name: ${filenameLabel}`);
  }
  if (uploadLabel) {
    if (state.linkType === 'generate') {
      parts.push('Generated when webhook runs');
    } else {
      parts.push('Saved on submit and update');
      parts.push(`Generate on print: ${state.linkUpload.generateOnPrint ? 'Yes' : 'No'}`);
    }
    if (type === 'docx') parts.push(`Lock tracked changes: ${state.linkUpload.lockTrackedChanges ? 'Yes' : 'No'}`);
  }
  return parts.join('; ');
}

function formatUploadSummaryFromLink(upload, linkType, targets, references, names) {
  if (!upload?.referenceId) return 'None';
  const refId = upload.referenceId;
  const targetName = targetNameForRef(targets, names, refId);
  const uploadLabel = fieldLabelFromRef(references, refId, upload.uploadFieldId);
  const filenameLabel = fieldLabelFromRef(references, refId, upload.filenameFieldId);
  if (!upload?.uploadFieldId && !upload?.filenameFieldId) return 'None';
  const type = uploadFormatLabel(upload.uploadType);
  const parts = [];
  if (uploadLabel) parts.push(`${targetName} → ${uploadLabel} (.${type})`);
  else if (filenameLabel) parts.push(`${targetName} → download name: ${filenameLabel}`);
  if (uploadLabel && filenameLabel) {
    parts.push(`download name: ${filenameLabel}`);
  }
  if (uploadLabel) {
    if (linkType === 'generate') {
      parts.push('Generated when webhook runs');
    } else {
      parts.push('Saved on submit and update');
      parts.push(`Generate on print: ${upload.genActions?.print ? 'Yes' : 'No'}`);
    }
    if (type === 'docx') parts.push(`Lock tracked changes: ${upload.lockTrackedChanges ? 'Yes' : 'No'}`);
  }
  return parts.join('; ');
}

function formatPasskeySummaryFromWizard(state) {
  const entries = (state.linkPasskeys || []).filter((p) => p.referenceId && p.passkeyField?.id != null);
  if (!entries.length) return 'None';
  const parts = entries.map((p) => {
    const targetName = targetNameForRef(state.targets, {}, p.referenceId);
    return formatPasskeyEntryLine(targetName, p.passkeyField.label, p.placeholder);
  });
  if (String(state.linkPasskeyMessage || '').trim()) parts.push('Custom message configured');
  return parts.join('; ');
}

function formatPasskeySummaryFromLink(passkey, targets, names, references) {
  const entries = passkey?.entries || [];
  if (!entries.length) return 'None';
  const parts = entries.map((entry) => {
    const targetName = targetNameForRef(targets, names, entry.referenceId);
    const fieldLabel = fieldLabelFromRef(references, entry.referenceId, entry.passkeyFieldId);
    return formatPasskeyEntryLine(targetName, fieldLabel, entry.passkeyPlaceholder);
  });
  if (String(passkey.passkeyMessage || '').trim()) parts.push('Custom message configured');
  return parts.join('; ');
}

function formatRedirectSummary(redirect, targets, names, references, onLoad) {
  if (!redirect?.referenceId || !redirect.fieldId) return 'None';
  const targetName = targetNameForRef(targets, names, redirect.referenceId);
  const fieldLabel = fieldLabelFromRef(references, redirect.referenceId, redirect.fieldId);
  return `${targetName} → ${fieldLabel}${onLoad ? ' (on load)' : ''}`;
}

function formatSuccessMessageSummary(successMessage, targets, names, references) {
  if (!successMessage?.referenceId || !successMessage.fieldId) return 'None';
  const targetName = targetNameForRef(targets, names, successMessage.referenceId);
  const fieldLabel = fieldLabelFromRef(references, successMessage.referenceId, successMessage.fieldId);
  return `${targetName} → ${fieldLabel}`;
}

function buildTargetsSummary(targets, names) {
  return (targets || []).map((t) => ({
    name: t.referenceName || names[t.referenceId] || t.referenceId,
    action: capitalizeAction(t.action),
    keyField: t.parentField?.label || '—',
    recordNotFound: (t.action === 'update' || t.action === 'print')
      ? recordNotFoundActionLabel(t.recordNotFoundAction)
      : '—',
    onEmptyRecord: (t.action === 'submit'
      || ((t.action === 'update' || t.action === 'print')
        && (t.recordNotFoundAction === 'create' || t.onEmptyRecordAction)))
      ? onEmptyRecordActionLabel(t.onEmptyRecordAction)
      : '—',
  }));
}

function buildTargetsSummaryFromLink(linkTargets, names, references, link) {
  return (linkTargets || []).map((t) => ({
    name: names[t.referenceId] || t.referenceId,
    action: capitalizeAction(t.action),
    keyField: t.parentFieldId != null
      ? hostFieldLabel(references, link, t.parentFieldId)
      : '—',
    recordNotFound: (t.action === 'update' || t.action === 'print')
      ? recordNotFoundActionLabel(t.recordNotFoundAction)
      : '—',
    onEmptyRecord: (t.action === 'submit'
      || ((t.action === 'update' || t.action === 'print')
        && (t.recordNotFoundAction === 'create' || t.onEmptyRecordAction)))
      ? onEmptyRecordActionLabel(t.onEmptyRecordAction)
      : '—',
  }));
}

function downloadOnOpenLabel(enabled) {
  return enabled ? 'Enabled' : 'Disabled';
}

function resolveLinkRecordId(link, fallbackLinkId) {
  const candidates = [link?.linkId, link?.id, fallbackLinkId];
  for (const value of candidates) {
    if (value == null) continue;
    const id = String(value).trim();
    if (id) return id;
  }
  return '';
}

function resolveWebhookPublicUrl(link) {
  const stored = link?.field?.publicUrl ? String(link.field.publicUrl).trim() : '';
  if (stored) return stored;
  const webhookId = link?.field?.id != null ? String(link.field.id).trim() : '';
  const tableId = link?.table?.id != null ? String(link.table.id).trim() : '';
  const realm = link?.realm != null ? String(link.realm).trim() : '';
  if (!webhookId || !tableId || !realm) return '';
  return `https://${realm}.quickbase.com/db/${tableId}?a=GenWebhookEdit&id=${webhookId}`;
}

function formatParseFieldSummary(parseField, hostTableId, references) {
  if (!parseField?.fieldId) return '—';
  const ref = (references || []).find((r) => String(r.tableId) === String(hostTableId));
  const label = ref?.tableSchema?.fields?.find(
    (f) => String(f.id) === String(parseField.fieldId)
  )?.label;
  return label || parseField.fieldId;
}

export default function SignedLinkSummaryBuilder() {
  return {
    fromWizardState(state) {
      const hostTable = (state.tables || []).find((t) => t.tableId === state.newField?.tableId);
      const names = {};
      (state.targets || []).forEach((t) => {
        names[t.referenceId] = t.referenceName || t.referenceId;
      });
      const redirectRefId = state.linkRedirect?.referenceId;
      const successRefId = state.linkSuccessMessage?.referenceId;
      const field = state.editLink?.field;

      const linkType = state.linkType || 'field';
      const downloadOnly = linkType === 'import' || !(state.targets || []).length;

      return {
        label: state.newField?.label || '',
        linkId: resolveLinkRecordId(state.editLink, state.editLinkId),
        type: linkType,
        hostTableName: linkType === 'url'
          ? 'URL'
          : (hostTable?.tableName || state.newField?.tableId || ''),
        fieldId: linkType === 'url'
          ? '—'
          : (field?.id ? String(field.id) : ''),
        publicUrl: linkType === 'url'
          ? ''
          : (field?.publicUrl ? String(field.publicUrl) : ''),
        downloadOnOpen: downloadOnOpenLabel(!!state.forcePdfDownload),
        enableSaveAndKeepWorking: (linkType === 'import' || linkType === 'generate')
          ? false
          : (downloadOnly ? false : state.enableSaveAndKeepWorking !== false),
        downloadOnly,
        targets: (downloadOnly || linkType === 'import') ? [] : buildTargetsSummary(state.targets, names),
        redirect: state.linkRedirect?.field
          ? formatRedirectSummary(
            { referenceId: redirectRefId, fieldId: state.linkRedirect.field.id },
            state.targets,
            names,
            state.references,
            state.redirectOnLoad
          )
          : 'None',
        redirectScreen: state.linkRedirect?.field && state.linkRedirect.customScreen && String(state.linkRedirect.message || '').trim()
          ? 'Custom'
          : '',
        upload: linkType === 'import' ? 'N/A' : formatUploadSummaryFromWizard(state),
        parseField: linkType === 'import'
          ? (state.parseDocField?.label || '—')
          : undefined,
        passkeys: formatPasskeySummaryFromWizard(state),
        successMessage: state.linkSuccessMessage?.field
          ? formatSuccessMessageSummary(
            { referenceId: successRefId, fieldId: state.linkSuccessMessage.field.id },
            state.targets,
            names,
            state.references
          )
          : 'None',
        prefills: (state.prefills || []).map((p) => ({
          targetTable: p.reference?.name || '',
          targetField: p.targetField?.label || '',
          sourceField: p.sourceField?.label || '',
        })),
      };
    },

    fromLinkListItem(link, references, fallbackLinkId) {
      const names = refNameById(references);
      const options = link?.options || {};
      const linkType = link?.type || 'field';
      const linkTargets = (link?.targets || []).map((t) => ({
        ...t,
        referenceName: names[t.referenceId],
      }));
      const field = link?.field;

      const downloadOnly = linkType === 'import' || !(link?.targets || []).length;

      return {
        label: link?.field?.label || '',
        linkId: resolveLinkRecordId(link, fallbackLinkId),
        type: linkType,
        hostTableName: linkType === 'url'
          ? 'URL'
          : (link?.table?.name || link?.table?.id || ''),
        fieldId: linkType === 'url' ? '—' : (field?.id ? String(field.id) : ''),
        publicUrl: linkType === 'url'
          ? ''
          : (linkType === 'generate' || linkType === 'import')
            ? resolveWebhookPublicUrl(link)
            : (field?.publicUrl ? String(field.publicUrl) : ''),
        downloadOnOpen: downloadOnOpenLabel(!!options.downloadOnOpen),
        enableSaveAndKeepWorking: (linkType === 'import' || linkType === 'generate')
          ? false
          : (downloadOnly ? false : options.enableSaveAndKeepWorking !== false),
        downloadOnly,
        targets: (downloadOnly || linkType === 'import') ? [] : buildTargetsSummaryFromLink(link?.targets, names, references, link),
        redirect: formatRedirectSummary(
          options.redirect,
          linkTargets,
          names,
          references,
          options.redirect?.onLoad
        ),
        redirectScreen: options.redirect?.referenceId && options.redirect?.fieldId
          && options.redirect.customScreen && String(options.redirect.message || '').trim()
          ? 'Custom'
          : '',
        upload: linkType === 'import'
          ? 'N/A'
          : formatUploadSummaryFromLink(
            options.upload,
            linkType,
            linkTargets,
            references,
            names
          ),
        parseField: linkType === 'import'
          ? formatParseFieldSummary(options.parseField, link?.table?.id, references)
          : undefined,
        passkeys: formatPasskeySummaryFromLink(options.passkey, linkTargets, names, references),
        successMessage: formatSuccessMessageSummary(
          options.successMessage,
          linkTargets,
          names,
          references
        ),
        prefills: (link?.prefills || []).map((p) => ({
          targetTable: names[p.referenceId] || '',
          targetField: fieldLabelFromRef(references, p.referenceId, p.targetFieldId),
          sourceField: hostFieldLabel(references, link, p.sourceFieldId),
        })),
      };
    },
  };
}
